import { DateTime } from 'luxon'

// Date-range presets for DateRangePicker.vue. Ranges are whole days, passed
// around as ISO dates ("yyyy-MM-dd") so they carry no time or timezone.
//
// A preset is a plain object, so pages can pass their own:
//   key       Unique id; emitted in the v-model as `preset`.
//   label     Button text.
//   pane      Right-hand pane: 'calendar' (default), 'halfMonth', or 'month'.
//   range     (today) => { start, end } as luxon DateTimes. Leave it out for a
//             manual preset, where the user picks the days themselves.
//   relative  The range moves with "today" (e.g. Last 7 days), so it is
//             re-run when today changes (midnight, or a new timezone) unless
//             the user has since picked a different period in its pane.

export const TODAY = {
    key: 'today',
    label: 'Today',
    relative: true,
    range: today => ({ start: today, end: today }),
}

export const YESTERDAY = {
    key: 'yesterday',
    label: 'Yesterday',
    relative: true,
    range: today => ({ start: today.minus({ days: 1 }), end: today.minus({ days: 1 }) }),
}

// Last `days` days, including today.
export function lastNDays(days, label = `Last ${days} days`) {
    return {
        key: `last${days}Days`,
        label,
        relative: true,
        range: today => ({ start: today.minus({ days: days - 1 }), end: today }),
    }
}

export const LAST_7_DAYS = lastNDays(7)

export const MONTH_TO_DATE = {
    key: 'monthToDate',
    label: 'Month-to-date',
    relative: true,
    range: today => ({ start: today.startOf('month'), end: today }),
}

// Opens on the half of the month that today is in; the pane picks other halves.
export const SEMI_MONTHLY = {
    key: 'semiMonthly',
    label: 'Semi-monthly',
    pane: 'halfMonth',
    relative: true,
    range: today => halfMonthRange(today.year, today.month, (today.day <= 15) ? 1 : 2),
}

// Opens on the current month; the pane picks other months.
export const MONTH = {
    key: 'month',
    label: 'Month',
    pane: 'month',
    relative: true,
    range: today => monthRange(today.year, today.month),
}

export const CUSTOM = {
    key: 'custom',
    label: 'Custom range',
}

export const CUSTOM_DAY = {
    key: 'custom',
    label: 'Pick a day',
}

// Built-in presets by key, so a blade can pass ["today", "last7Days", "custom"]
// where it cannot pass JS objects. "customDay" is CUSTOM_DAY (its key is also
// "custom"), and any "lastNDays" key (e.g. "last30Days") builds lastNDays(N).
const BUILT_IN_PRESETS = {
    today: TODAY,
    yesterday: YESTERDAY,
    monthToDate: MONTH_TO_DATE,
    semiMonthly: SEMI_MONTHLY,
    month: MONTH,
    custom: CUSTOM,
    customDay: CUSTOM_DAY,
}

// A preset object passes through; a key resolves to its built-in; an unknown
// key gives null.
export function resolvePreset(entry) {
    if (typeof entry !== 'string') {
        return entry ?? null
    }
    if (BUILT_IN_PRESETS[entry]) {
        return BUILT_IN_PRESETS[entry]
    }
    const lastDays = /^last(\d+)Days$/.exec(entry)
    if (lastDays && (Number(lastDays[1]) > 0)) {
        return lastNDays(Number(lastDays[1]))
    }
    return null
}

export const DEFAULT_PRESETS = [TODAY, YESTERDAY, LAST_7_DAYS, MONTH_TO_DATE, SEMI_MONTHLY, MONTH, CUSTOM]

export const DEFAULT_SINGLE_PRESETS = [TODAY, YESTERDAY, CUSTOM_DAY]

export function paneOf(preset) {
    return preset?.pane ?? 'calendar'
}

export function toIso(date) {
    return date.toISODate()
}

// Null for anything that is not a real "yyyy-MM-dd" date. Strict on purpose:
// fromISO also takes timestamps ("2026-10-06T04:00:00Z"), which it moves into
// the browser's zone, so the day can shift back by one.
export function fromIso(iso) {
    if (typeof iso !== 'string') {
        return null
    }
    const date = DateTime.fromFormat(iso, 'yyyy-MM-dd')
    return date.isValid ? date : null
}

// Today's calendar date in `timezone` (e.g. the company's), as a local-zone
// DateTime at midnight. Ranges are plain dates, so only the year/month/day of
// "now" in that zone matter; keeping them in the local zone means they line up
// with the calendar, which works in browser-local dates. A missing or invalid
// zone falls back to the browser's.
export function todayInZone(timezone, now = DateTime.now()) {
    const zoned = timezone ? now.setZone(timezone) : now
    const day = zoned.isValid ? zoned : now
    return DateTime.fromObject({ year: day.year, month: day.month, day: day.day })
}

// Half 1 is the 1st to the 15th; half 2 is the 16th to the end of the month.
export function halfMonthRange(year, month, half) {
    const first = DateTime.fromObject({ year, month, day: 1 })
    if (half === 1) {
        return { start: first, end: first.set({ day: 15 }) }
    }
    return { start: first.set({ day: 16 }), end: first.endOf('month').startOf('day') }
}

export function monthRange(year, month) {
    const first = DateTime.fromObject({ year, month, day: 1 })
    return { start: first, end: first.endOf('month').startOf('day') }
}

// Puts the earlier date first.
export function orderRange(range) {
    if (range.end < range.start) {
        return { start: range.end, end: range.start }
    }
    return range
}

// Pull both ends of a range back to `max`, so a range wholly after `max`
// collapses to that one day.
export function clampRange(range, max) {
    if (!max) {
        return range
    }
    return {
        start: (range.start > max) ? max : range.start,
        end: (range.end > max) ? max : range.end,
    }
}

// Times are wall-clock "HH:mm" strings (24-hour) in the picker's timezone.
// Presets cover whole days.
export const DAY_START = '00:00'
export const DAY_END = '23:59'

// Null for anything that is not a real 24-hour "HH:mm" time.
export function parseTime(time) {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time ?? '')
    if (!match) {
        return null
    }
    return { hours: Number(match[1]), minutes: Number(match[2]) }
}

export function timeFromParts(hours, minutes) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// "19:30" for '24h', "7:30 PM" for '12h'.
export function formatTime(time, timeFormat = '24h') {
    const parts = parseTime(time)
    if (!parts) {
        return ''
    }
    if (timeFormat !== '12h') {
        return time
    }
    return DateTime.fromObject(parts).toFormat('h:mm a')
}

// On a one-day range an end time before the start time is a reversed pick;
// swap them, the same way orderRange swaps reversed dates.
export function orderTimes(start, end, startTime, endTime) {
    if (start && end && start.hasSame(end, 'day') && (endTime < startTime)) {
        return { startTime: endTime, endTime: startTime }
    }
    return { startTime, endTime }
}

// With times: "Oct 1, 2026 07:00 - Oct 5, 2026 19:00", "Oct 5, 2026 07:00 - 19:00"
// within one day, or "Oct 5, 2026 07:00" for a single moment.
export function formatPeriodWithTimes(start, end, startTime, endTime, timeFormat = '24h') {
    if (!start) {
        return ''
    }
    const from = `${start.toFormat('LLL d, yyyy')} ${formatTime(startTime, timeFormat)}`
    if (!end || start.hasSame(end, 'day')) {
        return (startTime === endTime) ? from : `${from} - ${formatTime(endTime, timeFormat)}`
    }
    return `${from} - ${end.toFormat('LLL d, yyyy')} ${formatTime(endTime, timeFormat)}`
}

// "Oct 1, 2026 - Oct 15, 2026", "Oct 10, 2026" for one day, or "October 2026"
// when the range is exactly one whole month.
export function formatPeriod(start, end) {
    if (!start) {
        return ''
    }
    if (end && (start.day === 1) && start.hasSame(end, 'month') && end.hasSame(start.endOf('month'), 'day')) {
        return start.toFormat('LLLL yyyy')
    }
    if (!end || start.hasSame(end, 'day')) {
        return start.toFormat('LLL d, yyyy')
    }
    return `${start.toFormat('LLL d, yyyy')} - ${end.toFormat('LLL d, yyyy')}`
}
